import { useState, useEffect } from "react";

export const useScrollObserver = (containerRef, latestResponseRef) => {
  // Start with button hidden until we determine its visibility
  const [showScrollButton, setShowScrollButton] = useState(false);

  useEffect(() => {
    // Find the actual scrollable parent
    const findScrollableParent = (element) => {
      if (!element) return document.documentElement;
      
      const style = window.getComputedStyle(element);
      const overflowY = style.getPropertyValue('overflow-y');
      
      if (overflowY === 'auto' || overflowY === 'scroll') {
        return element;
      }
      
      return findScrollableParent(element.parentElement);
    };
    
    // The real scrollable container might be a parent of our ref
    const getScrollableContainer = () => {
      if (!latestResponseRef?.current) return null;
      return findScrollableParent(latestResponseRef.current);
    };
    
    const checkVisibility = () => {
      if (!latestResponseRef?.current) {
        setShowScrollButton(false);
        return;
      }
      
      const scrollContainer = getScrollableContainer();
      if (!scrollContainer) {
        setShowScrollButton(false);
        return;
      }
      
      const containerRect = scrollContainer.getBoundingClientRect();
      const elementRect = latestResponseRef.current.getBoundingClientRect();
      
      // Calculate if the element is fully visible in the container's viewport
      // We check if the bottom of the element is above the bottom of the container's visible area
      const isFullyVisible = elementRect.bottom <= containerRect.bottom;
      
      // Show button only if the element is NOT fully visible
      setShowScrollButton(!isFullyVisible);
    };
    
    // Initial check - wait a moment for rendering to complete
    setTimeout(checkVisibility, 100);
    
    // Get the actual scrollable container
    const scrollContainer = getScrollableContainer();
    if (!scrollContainer) return;
    
    // Add event listener to the actual scrollable container
    scrollContainer.addEventListener('scroll', checkVisibility);
    window.addEventListener('resize', checkVisibility);
    
    // Re-check visibility whenever the latest response reference changes
    if (latestResponseRef?.current) {
      checkVisibility();
    }
    
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', checkVisibility);
      }
      window.removeEventListener('resize', checkVisibility);
    };
  }, [latestResponseRef, latestResponseRef?.current]);
  
  return { showScrollButton };
};